import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  selectCartEventHasUnbundledAddonsOrTopUps,
  selectHasRegistrationForm,
  selectWalletCredit,
} from 'store/cartSlice/selectors';
import CartStepsSkeleton from '../Skeletons/Common/CartStepsSkeleton';
import CartStep from './CartStep';

interface CartStepsProps {
  eventHasRegistrationForms?: boolean;
  displayAddonsStep?: boolean;
  isLoading?: boolean;
}

const CartSteps: React.FC<CartStepsProps> = ({
  eventHasRegistrationForms,
  displayAddonsStep,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const hasRegistrationForm = useSelector(selectHasRegistrationForm);

  const cartEventHasUnbundledAddonsOrTopUps = useSelector(
    selectCartEventHasUnbundledAddonsOrTopUps,
  );

  const addonsStepShouldBeDisplayed =
    displayAddonsStep || cartEventHasUnbundledAddonsOrTopUps;

  useEffect(() => {
    let counter = 0;
    if (addonsStepShouldBeDisplayed) {
      counter++;
    }
    if (pathname.includes('/addons') && addonsStepShouldBeDisplayed) {
      setCurrentStep(1);
    } else if (pathname.includes('/billing-information')) {
      setCurrentStep(1 + counter);
    } else if (pathname.includes('/order/assign-tickets')) {
      setCurrentStep(2 + counter);
    } else if (pathname.includes('/order/confirmation')) {
      setCurrentStep(3 + counter);
    }
  }, [pathname, addonsStepShouldBeDisplayed]);

  const steps = [t('cartSteps.billingInfo'), t('cartSteps.orderConfirmation')];

  if (hasRegistrationForm || eventHasRegistrationForms) {
    steps.splice(1, 0, t('cartSteps.assignTickets'));
  }

  if (addonsStepShouldBeDisplayed) {
    steps.splice(0, 0, t('cartSteps.addonsAndTopUp'));
  }

  return isLoading ? (
    <CartStepsSkeleton />
  ) : (
    <div className="stepper-container flex mx-auto w-fit flex-col md:flex-row">
      {steps.map((step, key) => {
        if (
          key + 1 < currentStep ||
          (key + 1 === currentStep && step === t('cartSteps.orderConfirmation'))
        )
          return (
            <CartStep
              key={steps[key]}
              status="completed"
              name={steps[key]}
              stepNumber={key + 1}
              isFinalStep={key === steps.length - 1}
            />
          );
        if (key + 1 === currentStep)
          return (
            <CartStep
              key={steps[key]}
              status="current"
              name={steps[key]}
              stepNumber={key + 1}
              isFinalStep={key === steps.length - 1}
            />
          );
        return (
          <CartStep
            key={steps[key]}
            status="next"
            name={steps[key]}
            stepNumber={key + 1}
            isFinalStep={key === steps.length - 1}
          />
        );
      })}
    </div>
  );
};

export default CartSteps;
